import React, { useState, useEffect } from 'react';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import { getAreas, createArea, updateArea, deleteArea, Area } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface AreaManagementProps {
  skipLayout?: boolean;
}

const AreaManagement: React.FC<AreaManagementProps> = ({ skipLayout = false }) => {
  const { hasPermission } = useAuth();
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [formData, setFormData] = useState({
    name: '',
  });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ name?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  // Capitalize first letter of each word
  const capitalizeWords = (text: string): string => {
    return text
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    fetchAreas(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search term - wait 800ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Refetch when debounced search changes
  useEffect(() => {
    fetchAreas(1, pageSize, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const fetchAreas = async (page: number, size: number = pageSize, search: string = debouncedSearchTerm) => {
    try {
      setLoading(true);
      const response = await getAreas(page, size, search && search.trim() ? search.trim() : undefined);
      setAreas(response.data.data || []);

      const pagination = response.data.pagination || {};
      setCurrentPage(pagination.currentPage || page);
      setTotalItems(pagination.totalItems || 0);
      setHasNextPage(pagination.hasNextPage || false);
      setHasPreviousPage(pagination.hasPreviousPage || false);
    } catch (err: any) {
      setError('Failed to load areas');
      console.error('Failed to load areas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (area?: Area) => {
    if (area) {
      setEditingArea(area);
      setFormData({
        name: area.metadata.name,
      });
    } else {
      setEditingArea(null);
      setFormData({
        name: '',
      });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setValidationErrors({});
    setSubmitting(true);

    if (!formData.name.trim()) {
      setValidationErrors({ name: 'Area name is required' });
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    try {
      if (editingArea) {
        const updateData = {
          metadata: {
            name: formData.name.trim(),
          },
        };
        await updateArea(editingArea._id, updateData);
      } else {
        const data = {
          metadata: {
            name: formData.name.trim(),
          },
        };
        await createArea(data);
      }

      setShowModal(false);
      fetchAreas(currentPage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    setAreaToDelete({ id, name });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!areaToDelete) return;

    try {
      await deleteArea(areaToDelete.id);
      const nextPage =
        currentPage > 1 && areas.length === 1 ? currentPage - 1 : currentPage;
      fetchAreas(nextPage);
      setShowDeleteConfirm(false);
      setAreaToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete area. Unknown error occurred.');
      setShowDeleteConfirm(false);
      setAreaToDelete(null);
      setShowErrorModal(true);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchAreas(currentPage, pageSize);
  };

  const content = (
    <>
      {/* <div className="page-header">
        <h2>Area Management</h2>
        <p>Manage areas for organizing ambulances</p>
      </div> */}

      <div className="card">
        <div className="card-header">
          {/* <h3 className="card-title">Areas</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search areas..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
              }}
            />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('area.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Area
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading areas...</div>
        ) : areas.length === 0 ? (
          <div className="empty-state">
            <h3>No areas yet</h3>
            <p>Create your first area to get started</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 450px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Name</th>
                    <th>Ambulances</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {areas.map((area) => {
                    const ambulanceCount = area.spec.ambulances?.length || 0;
                    return (
                      <tr key={area._id}>
                        <td>
                          <strong>{area.metadata.name}</strong>
                        </td>
                        <td>
                          {ambulanceCount > 0 ? (
                            <span style={{ fontSize: '16px' }}>
                              {ambulanceCount}
                            </span>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>No ambulances</span>
                          )}
                        </td>
                        <td>
                          {formatDateDDMMYYYY(area.metadata.createdAt)}
                        </td>
                        <td>
                          <div className="action-buttons">
                            {hasPermission('area.update') && (
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => handleOpenModal(area)}
                              >
                                Edit
                              </button>
                            )}
                            {hasPermission('area.delete') && (
                              <button
                                className="btn-icon btn-delete"
                                onClick={() =>
                                  handleDelete(
                                    area._id,
                                    area.metadata.name
                                  )
                                }
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = Number(e.target.value);
                      setPageSize(newSize);
                      fetchAreas(1, newSize);
                    }}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100, 500].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && fetchAreas(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && fetchAreas(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {editingArea ? 'Edit Area' : 'Create New Area'}
              </h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label>
                    Area Name
                    <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      const capitalized = capitalizeWords(e.target.value);
                      setFormData({ ...formData, name: capitalized });
                    }}
                    className={validationErrors.name ? 'input-error' : ''}
                    placeholder="e.g., North Zone, South Zone, Downtown"
                  />
                  {validationErrors.name && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.name}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px' }}>
                    Enter a unique name for this area. You can assign ambulances to this area in the Area Mapping tab.
                  </small>
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingArea ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    `${editingArea ? 'Update' : 'Create'} Area`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Area"
        message={`Are you sure you want to delete the area "${areaToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setAreaToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="danger"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default AreaManagement;

